// src/hooks/useSimpleDashboardData.ts
import { useState, useEffect } from 'react';
import { supabase } from '../supabase/config';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { useMultiGrid } from '../contexts/MultiGridContext';
import { intentionPeriodsService } from '../services/intentionPeriodsService';

export interface WorkflowStep {
  id: string;
  name: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'needs_attention';
  priority: 'low' | 'medium' | 'high' | 'critical';
  completionCriteria: string;
  actionUrl?: string;
}

export interface SystemAlert {
  id: string;
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  description: string;
  actionText?: string;
  actionUrl?: string;
  priority: number;
  createdAt: Date;
}

export interface DashboardStats {
  staff: {
    total: number;
    active: number;
    totalFTE: number;
    byRole: Record<string, number>;
  };
  workflow: {
    completedSteps: number;
    totalSteps: number;
    progressPercentage: number;
  };
  timetabling: {
    totalClasses: number;
    unassignedClasses: number;
    allocationPercentage: number;
    totalConflicts: number;
  };
  intentions: {
    totalStaff: number;
    submittedIntentions: number;
    submissionPercentage: number;
  };
}

export const useSimpleDashboardData = () => {
  const { user } = useAuth();
  const { staff, subjects, loading: baseLoading } = useData();
  const { getAllGridsStats, getCrossGridConflicts } = useMultiGrid();
  
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Define basic workflow steps with simple criteria
  const defineWorkflowSteps = (): WorkflowStep[] => [
    {
      id: 'admin_setup',
      name: 'Admin Users Setup',
      description: 'Configure system administrators',
      status: 'not_started',
      priority: 'critical',
      completionCriteria: 'At least one admin user configured',
      actionUrl: '/admin/school-settings'
    },
    {
      id: 'staff_import',
      name: 'Staff Data Import',
      description: 'Import or enter all staff members',
      status: 'not_started',
      priority: 'critical',
      completionCriteria: 'All teaching staff have complete profiles',
      actionUrl: '/settings/user-management'
    },
    {
      id: 'departments_setup',
      name: 'Department Configuration',
      description: 'Set up all school departments',
      status: 'not_started',
      priority: 'high',
      completionCriteria: 'All departments defined with heads assigned',
      actionUrl: '/admin/school-settings'
    },
    {
      id: 'subjects_import',
      name: 'Subject Configuration',
      description: 'Import and configure all subjects',
      status: 'not_started',
      priority: 'high',
      completionCriteria: 'All subjects mapped to departments',
      actionUrl: '/grid/subject-config'
    },
    {
      id: 'grid_import',
      name: 'Grid Data Import',
      description: 'Import timetable grid from TimeTabler',
      status: 'not_started',
      priority: 'high',
      completionCriteria: 'Grid data successfully imported with all classes',
      actionUrl: '/grid/import'
    },
    {
      id: 'curriculum_leaders',
      name: 'Curriculum Leaders',
      description: 'Assign curriculum area leaders',
      status: 'not_started',
      priority: 'medium',
      completionCriteria: 'All curriculum areas have assigned leaders',
      actionUrl: '/settings/user-management'
    },
    {
      id: 'teaching_intentions',
      name: 'Teaching Intentions',
      description: 'Collect staff teaching intentions',
      status: 'not_started',
      priority: 'medium',
      completionCriteria: 'All staff have submitted teaching intentions',
      actionUrl: '/intentions'
    },
    {
      id: 'staff_allocation',
      name: 'Staff Allocation',
      description: 'Assign teachers to classes',
      status: 'not_started',
      priority: 'high',
      completionCriteria: 'All classes have assigned teachers',
      actionUrl: '/grid/multi-editor'
    }
  ];

  // Calculate workflow status with error handling
  const calculateWorkflowStatus = async (): Promise<WorkflowStep[]> => {
    const steps = defineWorkflowSteps();
    
    try {
      console.log('Calculating workflow status...');

      // Check admin setup using Supabase
      const { data: admins, error: adminError } = await supabase
        .from('staff')
        .select('id')
        .eq('role', 'admin')
        .eq('status', 'active');
      
      const adminCount = admins?.length || 0;
      console.log(`Found ${adminCount} admin users`);
      
      const adminStep = steps.find(s => s.id === 'admin_setup');
      if (adminStep) {
        adminStep.status = adminCount > 0 ? 'completed' : 'needs_attention';
      }

      // Check staff import
      const staffStep = steps.find(s => s.id === 'staff_import');
      if (staffStep) {
        const activeStaff = (staff || []).filter(s => s.status === 'active');
        const staffWithCompleteProfiles = activeStaff.filter(s => 
          s.name && s.email && s.department && s.fte !== undefined
        );
        console.log(`Active staff: ${activeStaff.length}, Complete profiles: ${staffWithCompleteProfiles.length}`);
        
        if (activeStaff.length === 0) {
          staffStep.status = 'not_started';
        } else if (staffWithCompleteProfiles.length === activeStaff.length) {
          staffStep.status = 'completed';
        } else {
          staffStep.status = 'in_progress';
        }
      }

      // Check departments setup
      const deptStep = steps.find(s => s.id === 'departments_setup');
      if (deptStep) {
        const headsOfDept = (staff || []).filter(s => s.role === 'head_of_department' && s.status === 'active');
        console.log(`Heads of department: ${headsOfDept.length}`);
        deptStep.status = headsOfDept.length > 0 ? 'completed' : 'not_started';
      }

      // Check subjects configuration
      const subjectStep = steps.find(s => s.id === 'subjects_import');
      if (subjectStep) {
        const subjectsCount = subjects?.length || 0;
        console.log(`Subjects configured: ${subjectsCount}`);
        subjectStep.status = subjectsCount > 0 ? 'completed' : 'not_started';
      }

      // Check curriculum leaders
      const curriculumStep = steps.find(s => s.id === 'curriculum_leaders');
      if (curriculumStep) {
        const curriculumLeaders = (staff || []).filter(s => s.role === 'curriculum_leader' && s.status === 'active');
        console.log(`Curriculum leaders: ${curriculumLeaders.length}`);
        curriculumStep.status = curriculumLeaders.length > 0 ? 'completed' : 'not_started';
      }

      // Check grid import (simplified check)
      const gridStep = steps.find(s => s.id === 'grid_import');
      if (gridStep) {
        try {
          const { data: grids, error: gridError } = await supabase
            .from('grids')
            .select('id');
          
          const gridCount = grids?.length || 0;
          console.log(`Grid data records: ${gridCount}`);
          gridStep.status = gridCount > 0 ? 'completed' : 'not_started';
        } catch (gridError) {
          console.log('Grid data collection not accessible, marking as not started');
          gridStep.status = 'not_started';
        }
      }

      // For other steps, keep them as not_started for now
      console.log('Workflow calculation completed');
      return steps;

    } catch (error) {
      console.error('Error calculating workflow status:', error);
      setError(`Failed to calculate workflow status: ${error}`);
      return steps; // Return basic steps even if calculation fails
    }
  };

  // Calculate stats including new timetabling and intentions data
  const calculateStats = async (): Promise<DashboardStats> => {
    const activeStaff = (staff || []).filter(s => s.status === 'active');
    const totalFTE = activeStaff.reduce((sum, s) => sum + (s.fte || 0), 0);

    const byRole: Record<string, number> = {};
    activeStaff.forEach(s => {
      const role = s.role || 'teacher';
      byRole[role] = (byRole[role] || 0) + 1;
    });

    const completedSteps = workflowSteps.filter(step => step.status === 'completed').length;
    const totalSteps = workflowSteps.length;
    const progressPercentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

    // Get timetabling statistics
    const gridStats = getAllGridsStats();
    const conflicts = getCrossGridConflicts();
    const allocationPercentage = gridStats.totalClasses > 0
      ? Math.round(((gridStats.totalClasses - gridStats.unassignedClasses) / gridStats.totalClasses) * 100)
      : 0;

    // Get teaching intentions statistics
    let intentionsStats = {
      totalStaff: activeStaff.length,
      submittedIntentions: 0,
      submissionPercentage: 0
    };

    // Teaching-intention response rate — identical definition and number to
    // Review Intentions (teaching-staff denominator, non-draft numerator, deduped;
    // see utils/intentionStats), scoped to the current or most-recent period.
    try {
      // Use the current open period if one is running; otherwise fall back to the
      // most recent period that actually has submissions (getAllPeriods is ordered
      // newest-first). This skips empty/duplicate periods that would read 0%.
      const openPeriod = await intentionPeriodsService.getCurrentOpenPeriod();
      const candidates = openPeriod
        ? [openPeriod]
        : await intentionPeriodsService.getAllPeriods();

      for (const period of candidates) {
        const stats = await intentionPeriodsService.getPeriodStats(period.id);
        if (stats && (stats.totalSubmissions > 0 || candidates.length === 1)) {
          intentionsStats = {
            totalStaff: stats.totalStaff,
            submittedIntentions: stats.totalSubmissions,
            submissionPercentage: stats.submissionRate
          };
          break;
        }
      }
    } catch (error) {
      console.log('Could not fetch teaching intentions data:', error);
    }

    return {
      staff: {
        total: (staff || []).length,
        active: activeStaff.length,
        totalFTE,
        byRole
      },
      workflow: {
        completedSteps,
        totalSteps,
        progressPercentage
      },
      timetabling: {
        totalClasses: gridStats.totalClasses,
        unassignedClasses: gridStats.unassignedClasses,
        allocationPercentage,
        totalConflicts: conflicts.length
      },
      intentions: intentionsStats
    };
  };

  // Generate simple alerts
  const generateAlerts = (calculatedStats: DashboardStats): SystemAlert[] => {
    const alerts: SystemAlert[] = [];

    // Check for critical setup steps
    const criticalIncomplete = workflowSteps.filter(s => 
      s.priority === 'critical' && s.status !== 'completed'
    );
    
    if (criticalIncomplete.length > 0) {
      alerts.push({
        id: 'critical_setup',
        type: 'error',
        title: 'Critical Setup Required',
        description: `${criticalIncomplete.length} critical setup steps need attention`,
        actionText: 'View Setup Progress',
        actionUrl: '/dashboard#workflow',
        priority: 100,
        createdAt: new Date()
      });
    }

    // Check for staff data
    if (calculatedStats.staff.active === 0) {
      alerts.push({
        id: 'no_staff',
        type: 'error',
        title: 'No Staff Data',
        description: 'Import staff data to begin timetabling',
        actionText: 'Add Staff',
        actionUrl: '/settings/user-management',
        priority: 90,
        createdAt: new Date()
      });
    }

    // Check for low completion rate
    if (calculatedStats.workflow.progressPercentage < 25) {
      alerts.push({
        id: 'low_progress',
        type: 'warning',
        title: 'Setup Incomplete',
        description: `System setup is only ${calculatedStats.workflow.progressPercentage}% complete`,
        actionText: 'Open Setup Wizard',
        actionUrl: '/dashboard?section=workflow',
        priority: 70,
        createdAt: new Date()
      });
    }

    return alerts.sort((a, b) => b.priority - a.priority);
  };

  // Main effect to load data
  useEffect(() => {
    const loadDashboardData = async () => {
      if (baseLoading || !user) {
        console.log('Waiting for base data to load...');
        return;
      }

      console.log('Loading dashboard data...');
      setLoading(true);
      setError(null);

      try {
        const steps = await calculateWorkflowStatus();
        setWorkflowSteps(steps);

        const calculatedStats = await calculateStats();
        setStats(calculatedStats);

        const generatedAlerts = generateAlerts(calculatedStats);
        setAlerts(generatedAlerts);

        console.log('Dashboard data loaded successfully');
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(`Failed to load dashboard data: ${err}`);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [staff, subjects, baseLoading, user]);

  // Update stats when workflow steps change
  useEffect(() => {
    if (workflowSteps.length > 0 && stats) {
      const updateStats = async () => {
        const calculatedStats = await calculateStats();
        setStats(calculatedStats);
        setAlerts(generateAlerts(calculatedStats));
      };
      updateStats();
    }
  }, [workflowSteps]);

  return {
    workflowSteps,
    stats,
    alerts,
    loading: loading || baseLoading,
    error,
    refresh: async () => {
      console.log('Refreshing dashboard data...');
      const steps = await calculateWorkflowStatus();
      setWorkflowSteps(steps);

      const calculatedStats = await calculateStats();
      setStats(calculatedStats);
      setAlerts(generateAlerts(calculatedStats));
    }
  };
};